/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import MsDyn365, { ICoreContext, IGridSettings, IImageSettings, ITelemetry, isChannelTypeB2B } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import {
    AsyncResult,
    CartLine,
    ChannelDeliveryOptionConfiguration,
    OrgUnitLocation,
    ProductAvailableQuantity,
    ProductDeliveryOptions,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import {
    CartLineValidationResultsByLineId,
    ProductCatalog,
    ReleasedProductType
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import {
    ArrayExtensions,
    getProductUrlSync,
    IProductInventoryInformation,
    ObjectExtensions,
    StringExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { getProductByProductId, getProductByProductIdAndWarehouse } from '@msdyn365-commerce-modules/cart';
import CartLineItemComponent, { ICartlineResourceString } from '../../../shared/components/cartlineitem.component';

export interface IFlyoutCartLineItemsProps {
    cartlines: CartLine[];
    cartState: ICartState | undefined;
    orgUnitLocations: OrgUnitLocation[] | undefined;
    resources: ICartlineResourceString;
    productAvailabilites: IProductInventoryInformation[] | undefined;
    products: SimpleProduct[] | undefined;
    productDeliveryOptions: ProductDeliveryOptions[] | undefined;
    pickupDeliveryModeCode?: string;
    catalogs?: ProductCatalog[];
    productAvailability?: AsyncResult<IProductInventoryInformation[]>;
    includeErrors?: boolean;
    cartlinesErrors: CartLineValidationResultsByLineId;

    /**
     * GridSettings for the product image in cartline
     */
    gridSettings: IGridSettings;

    /**
     * ImageSettings for the product image in cartline
     */
    imageSettings: IImageSettings;
    id: string;
    typeName: string;
    context: ICoreContext;
    telemetry: ITelemetry;
    removeButtonText: string;
    outOfStockText: string;
    outOfRangeOneText: string;
    outOfRangeFormatText: string;
    maxCartlineQuantity: number;
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
    isQuantityLimitsFeatureEnabled: boolean;
    outOfStockThreshold: number;
    isStockCheckEnabled: boolean;
    telemetryContent?: ITelemetryContent;
    removeItemClickHandler(cartlineToRemove: CartLine): void;
    updateCartLinesQuantitySuccessHandler(cartline: CartLine, quantity: number, lineIndex?: number): boolean;
}

export interface IFlyoutCartLineItemViewProps {
    cartline: React.ReactNode;
    remove: React.ReactNode;
    storeLocation: React.ReactNode;
    hasError: boolean;
    error?: string;

    data: {
        product: SimpleProduct;
        cartline: CartLine;
    };
}

const _getCartItemAvailableQuantity = (
    isStockCheckEnabled: boolean,
    outOfStockThreshold: number,
    productAvailability?: ProductAvailableQuantity
): number => {
    if (isStockCheckEnabled) {
        if (
            !productAvailability ||
            !productAvailability.AvailableQuantity ||
            productAvailability.AvailableQuantity <= 0 ||
            productAvailability.AvailableQuantity <= outOfStockThreshold
        ) {
            return 0;
        }
        return productAvailability.AvailableQuantity - outOfStockThreshold;
    }

    return 0;
};

const _getCartItemMaxQuantity = (
    maxQuantityByConfig: number,
    isStockCheckEnabled: boolean,
    availableQuantityInStock: number,
    isQuantityLimitsFeatureEnabled: boolean,
    maxByQuantityLimitsFeature: number
) => {
    if (isQuantityLimitsFeatureEnabled) {
        let maxByQuantityLimitsFeatureResult = maxByQuantityLimitsFeature;

        // If max by feature in not defined when feature is on then we suggest that there is no max by feature
        // and consider available qty if stock check enabled and max from config in site settings.
        if (!maxByQuantityLimitsFeature) {
            maxByQuantityLimitsFeatureResult = maxQuantityByConfig || 10;
        }

        return isStockCheckEnabled
            ? maxByQuantityLimitsFeatureResult < availableQuantityInStock
                ? maxByQuantityLimitsFeatureResult
                : availableQuantityInStock
            : maxByQuantityLimitsFeatureResult;
    }
    if (isStockCheckEnabled) {
        return availableQuantityInStock < maxQuantityByConfig ? availableQuantityInStock : maxQuantityByConfig;
    }
    return maxQuantityByConfig;
};

const _getErrorMessage = (
    availableQuantityInStock: number,
    currentQuantity: number,
    props: IFlyoutCartLineItemsProps,
    shouldSkipStockCheck: boolean,
    cartLine: CartLine,
    lineId: string | undefined,
    foundProductAvailability?: IProductInventoryInformation
): string | undefined => {
    const finalErrorMessages: string[] = [];

    const isLoadingDeliveryOptions = props.productAvailability?.status === 'LOADING';
    if (isLoadingDeliveryOptions) {
        return undefined;
    }
    // check availability passed as prop for each cartline
    if (props.productAvailabilites && ArrayExtensions.hasElements(props.productAvailabilites)) {
        if (foundProductAvailability === undefined) {
            finalErrorMessages.push(props.outOfStockText);
            return finalErrorMessages.toString();
        }
    }
    if (props.includeErrors && props.isStockCheckEnabled && !shouldSkipStockCheck) {
        if (availableQuantityInStock <= 0) {
            finalErrorMessages.push(props.outOfStockText);
        } else if (availableQuantityInStock < currentQuantity) {
            if (availableQuantityInStock === 1) {
                finalErrorMessages.push(props.outOfRangeOneText);
            } else {
                finalErrorMessages.push(props.outOfRangeFormatText.replace('{numLeft}', availableQuantityInStock.toString()));
            }
        }
    }

    // Server-side validation
    const checkForErrors = props.cartlinesErrors.ValidationResultsPairs;
    const errorsFoundByLineId = checkForErrors?.filter(index => index.LineId === lineId);
    if (errorsFoundByLineId !== undefined) {
        for (const i of errorsFoundByLineId) {
            if (ArrayExtensions.hasElements(i.ValidationFailures)) {
                i.ValidationFailures.forEach(validation => {
                    if (validation.ErrorContext !== undefined) {
                        finalErrorMessages.push(validation.ErrorContext);
                    }
                });
            }
        }
    }

    if (ArrayExtensions.hasElements(finalErrorMessages)) {
        return finalErrorMessages.join(' ');
    }

    return undefined;
};

/**
 * On Remove Click functionality.
 * @param removeItemClickHandler -Remove item click function.
 * @param cartline -CartLine.
 * @returns Remove change value.
 */
const onRemoveClickFunction = (removeItemClickHandler: (cartlineToRemove: CartLine) => void, cartline: CartLine) => () => {
    removeItemClickHandler(cartline);
};
const _assembleNode = (
    cartline: CartLine,
    product: SimpleProduct,
    props: IFlyoutCartLineItemsProps,
    index: number,
    foundProductAvailability?: IProductInventoryInformation,
    foundProductDeliveryOptions?: ProductDeliveryOptions
): IFlyoutCartLineItemViewProps => {
    const { imageSettings, gridSettings, id, typeName, context, resources, removeButtonText, removeItemClickHandler } = props;

    const availableQuantityInStock = _getCartItemAvailableQuantity(
        props.isStockCheckEnabled,
        props.outOfStockThreshold,
        foundProductAvailability?.ProductAvailableQuantity
    );
    const maxQuantity =
        product &&
        _getCartItemMaxQuantity(
            props.maxCartlineQuantity,
            props.isStockCheckEnabled,
            availableQuantityInStock,
            props.isQuantityLimitsFeatureEnabled,
            product?.Behavior?.MaximumQuantity || 0
        );

    // Check if the product is service or not by product type
    const isServiceItem = product.ItemTypeValue === ReleasedProductType.Service;

    const shouldSkipStockCheck = product?.ItemTypeValue === ReleasedProductType.Service || !!cartline.IsInvoiceLine;

    const errorMessage = ObjectExtensions.isNullOrUndefined(maxQuantity)
        ? undefined
        : _getErrorMessage(
              availableQuantityInStock,
              cartline.Quantity!,
              props,
              shouldSkipStockCheck,
              cartline,
              cartline.LineId,
              foundProductAvailability
          );

    const onRemoveClickHandler = onRemoveClickFunction(removeItemClickHandler, cartline);
    const payLoad = getPayloadObject('click', props.telemetryContent!, TelemetryConstant.RemoveCartItem);
    const removeCartItemAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);

    let productUrl = getProductUrlSync(product, props.context.actionContext, undefined);
    if (MsDyn365.isBrowser && isChannelTypeB2B(props.context.actionContext.requestContext)) {
        const fullUrl = new URL(productUrl, window.location.href);
        fullUrl.searchParams.set('catalogid', `${cartline.CatalogId ?? 0}`);
        productUrl = fullUrl.href;
    }

    return {
        data: {
            product,
            cartline
        },
        error: errorMessage,
        hasError:
            !isServiceItem && props.isStockCheckEnabled
                ? cartline.Quantity! > maxQuantity
                : false || (!ObjectExtensions.isNullOrUndefined(errorMessage) && !StringExtensions.isNullOrEmpty(errorMessage)),
        cartline: (
            <CartLineItemComponent
                data={{
                    cartLine: cartline,
                    product,
                    catalogs: props.catalogs,
                    cartState: props.cartState
                }}
                currentQuantity={cartline.Quantity}
                maxQuantity={maxQuantity}
                isOutOfStock={!isServiceItem && props.isStockCheckEnabled ? availableQuantityInStock <= 0 : false}
                gridSettings={gridSettings}
                imageSettings={imageSettings}
                id={id}
                typeName={typeName}
                productUrl={productUrl}
                context={context}
                resources={resources}
                key={`${index}-${productUrl}`}
                isQuantityEditable={!ObjectExtensions.isNullOrUndefined(product)}
                quantityOnChange={props.updateCartLinesQuantitySuccessHandler}
                primaryImageUrl={product.PrimaryImageUrl}
                errorMessage={errorMessage}
                isCartStateReady={props.cartState?.status === 'READY'}
                telemetryContent={props.telemetryContent}
                channelDeliveryOptionConfig={props.channelDeliveryOptionConfig}
                deliveryLocation={_renderDeliveryLocation(cartline, props)}
            />
        ),
        remove: (
            <Button
                className='msc-cart-line__remove-item'
                onClick={onRemoveClickHandler}
                title={removeButtonText}
                {...removeCartItemAttribute}
            >
                {removeButtonText}
            </Button>
        ),
        storeLocation: _renderStoreLocation(cartline, props)
    };
};

const _renderDeliveryLocation = (cartLine: CartLine | null | undefined, props: IFlyoutCartLineItemsProps): string | undefined => {
    let deliverylocation;
    if (cartLine && cartLine.FulfillmentStoreId) {
        const orgUnitName = _getOrgUnitName(cartLine.FulfillmentStoreId, props.orgUnitLocations);

        let foundProductDeliveryOption;
        if (props.productDeliveryOptions && props.productDeliveryOptions.length > 0) {
            foundProductDeliveryOption = props.productDeliveryOptions.find(deliveryOption => {
                return deliveryOption && deliveryOption.ProductId === cartLine.ProductId;
            });
        }
        const delivery = foundProductDeliveryOption?.DeliveryOptions?.find(option => option.Code === cartLine.DeliveryMode);
        const location = delivery?.Description;

        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        if (location) {
            deliverylocation = `${location}, ${orgUnitName}`;
        } else {
            deliverylocation = `${orgUnitName}`;
        }
    } else {
        deliverylocation = props.resources.shippingText;
    }
    return deliverylocation;
};

const _renderStoreLocation = (cartLine: CartLine | null | undefined, props: IFlyoutCartLineItemsProps): JSX.Element | null => {
    if (cartLine && cartLine.FulfillmentStoreId) {
        const orgUnitName = _getOrgUnitName(cartLine.FulfillmentStoreId, props.orgUnitLocations);

        if (orgUnitName) {
            return (
                <div className='msc-cart-line__bopis-method'>
                    <span className='pick-up'>{orgUnitName}</span>
                </div>
            );
        }
    }

    return null;
};

const _getOrgUnitName = (fulfillmentStoreId: string | undefined, orgUnitLocations: OrgUnitLocation[] | undefined) => {
    if (!orgUnitLocations || !fulfillmentStoreId || orgUnitLocations.length === 0) {
        return '';
    }

    const foundLocation = orgUnitLocations.find(orgUnitLocation => {
        return orgUnitLocation.OrgUnitNumber === fulfillmentStoreId;
    });

    if (foundLocation) {
        return foundLocation.OrgUnitName;
    }
    return fulfillmentStoreId;
};

const _assembleCartlines = (
    cartlines: CartLine[],
    products: SimpleProduct[] | undefined,
    props: IFlyoutCartLineItemsProps
): IFlyoutCartLineItemViewProps[] | null => {
    const reactNodes: IFlyoutCartLineItemViewProps[] = [];

    if (!products || products.length === 0) {
        //props.context.telemetry.error('Cartlines content is empty, module wont render');
        return null;
    }

    cartlines.map((cartline, index) => {
        let product;
        if (props.isQuantityLimitsFeatureEnabled) {
            // When feature is enabled the same products could have different quantity limits in Behavior so we need
            // to check productId and WarehouseId for identification.
            product = getProductByProductIdAndWarehouse(cartline.ProductId, products, cartline.WarehouseId, props.cartState);
        } else {
            product = getProductByProductId(cartline.ProductId, products);
        }
        let foundProductAvailability;
        if (props.productAvailabilites && props.productAvailabilites.length > 0) {
            foundProductAvailability = props.productAvailabilites.find(productAvailability => {
                return productAvailability.ProductAvailableQuantity?.ProductId! === cartline.ProductId;
            });
        }

        let foundProductDeliveryOption;
        if (props.productDeliveryOptions && props.productDeliveryOptions.length > 0) {
            foundProductDeliveryOption = props.productDeliveryOptions.find(deliveryOption => {
                return deliveryOption && deliveryOption.ProductId === cartline.ProductId;
            });
        }

        if (product) {
            reactNodes.push(_assembleNode(cartline, product, props, index, foundProductAvailability, foundProductDeliveryOption));
        }
    });

    return reactNodes;
};

/**
 * CartLineItems component.
 */

export const FlyoutCartLineItems = (props: IFlyoutCartLineItemsProps) => {
    const { products, cartlines } = props;
    return _assembleCartlines(cartlines, products, props);
};
